import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Box, Stack, Button, Tooltip, TextField, IconButton, DialogActions, Typography, Alert } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Iconify from '../iconify';
import { useSelector } from '../../redux/store';
import FormProvider, { RHFSwitch, RHFAutocomplete } from '../hook-form';

const startOfToday = new Date();
startOfToday.setHours(0, 0, 0, 0);
const DATE_TIME_OPTIONS = ['year', 'month', 'day', 'hours', 'minutes'];

const getInitialValues = (event, range, facilities, vendors) => {
  const initialEvent = {
    weekly: true,
    shipDate: startOfToday,
  };

  if (event) {
    initialEvent.cutoffStart = event.cutoffStartDate ? new Date(event.cutoffStartDate) : null;
    initialEvent.cutoffEnd = event.cutoffEndDate ? new Date(event.cutoffEndDate) : null;
    initialEvent.shipDate = event.shipDate ? new Date(event.shipDate) : null;
    initialEvent.weekly = event.weekly;
    initialEvent.biweekly = event.biweekly;
    initialEvent.vendor = vendors.find(v => v.id === event.vendorId);
    initialEvent.facility = facilities.find(f => f.value === event.facilityId);
  } else if (range) initialEvent.shipDate = new Date(range.start);
  return initialEvent;
};

CalendarForm.propTypes = {
  event: PropTypes.object,
  range: PropTypes.object,
  onClose: PropTypes.func,
  onDelete: PropTypes.func,
  handleEditAddDate: PropTypes.func,
  view: PropTypes.string,
  facility: PropTypes.object,
  vendor: PropTypes.object
};

export default function CalendarForm({ event, range, onDelete, onClose, handleEditAddDate, view, facility, vendor }) {

  const { data: { vendors, facilities } } = useSelector((state) => state.vendors);
  const methods = useForm({ defaultValues: getInitialValues(event, range, facilities, vendors) });
  const { reset, watch, control, handleSubmit, setValue, formState: { isSubmitting } } = methods;
  const values = watch();

  const isEdit = !!event;
  const monthCalendar = view === 'dayGridMonth';
  const hasDefaultVendor = monthCalendar && Boolean(vendor?.id);
  const hasDefaultFacility = (monthCalendar && Boolean(facility?.id) && facility.id !== 'all-facilities');

  const onSubmit = (data) => {
    handleEditAddDate({
      weekly: data.weekly,
      biweekly: data.biweekly,
      cutoffStart: data.cutoffStart,
      cutoffEnd: data.cutoffEnd,
      shipDate: data.shipDate,
      facilityId: data.facility?.value ?? event?.facilityId ?? facility?.id,
      vendorId: data.vendor?.id ?? event?.vendorId ?? vendor?.id
    });
    onClose();
    reset();
  };

  useEffect(() => {
    if (values.weekly) setValue('biweekly', false);
  }, [values.weekly]);

  useEffect(() => {
    if (values.biweekly) setValue('weekly', false);
  }, [values.biweekly]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={1.5} sx={{ px: 3 }}>
        {!hasDefaultVendor && <Box>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            Vendor
          </Typography>
          <RHFAutocomplete
            name="vendor"
            options={vendors.map((vendor) => ({ id: vendor.id, label: vendor.name }))}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off" />}
            sx={{
              mt: 0.5,
              '& legend': { display: 'none' },
              '& fieldset': { top: 0 },
              '& .MuiFormLabel-root ': { display: 'none' },
            }}
            disableClearable
          />
        </Box>}
        {!hasDefaultFacility && <Box>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            Facility
          </Typography>
          <RHFAutocomplete
            name="facility"
            options={facilities}
            renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off" />}
            sx={{
              mt: 0.5,
              '& legend': { display: 'none' },
              '& fieldset': { top: 0 },
              '& .MuiFormLabel-root ': { display: 'none' },
            }}
            disableClearable
          />
        </Box>}
        <Controller
          name="cutoffStart"
          control={control}
          render={({ field }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: .5 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Cutoff Start Time
              </Typography>
              <DateTimePicker
                {...field}
                slotProps={{ textField: { size: 'small' } }}
                defaultValue={values.cutoffStart}
                views={DATE_TIME_OPTIONS}
                onChange={(newValue) => field.onChange(newValue)}
                format={values.weekly || values.biweekly ? "EEEE MM/dd/yyyy h:mm a" : "MM/dd/yyyy h:mm a"}
              />
            </Box>
          )}
        />
        <Controller
          name="cutoffEnd"
          control={control}
          render={({ field }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: .5 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Cutoff End Time
              </Typography>

              <DateTimePicker
                {...field}
                slotProps={{ textField: { size: 'small' } }}
                defaultValue={values.cutoffEnd}
                views={DATE_TIME_OPTIONS}
                onChange={(newValue) => field.onChange(newValue)}
                sx={{ mt: 0 }} // Adjust margin if needed
                format={values.weekly || values.biweekly ? "EEEE MM/dd/yyyy h:mm a" : "MM/dd/yyyy h:mm a"}
              />
            </Box>
          )}
        />
        <Controller
          name="shipDate"
          control={control}
          render={({ field }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: .5 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Ship Date
              </Typography>
              <DateTimePicker
                {...field}
                slotProps={{ textField: { size: 'small' } }}
                defaultValue={values.shipDate}
                views={['year', 'month', 'day']}
                onChange={(newValue) => field.onChange(newValue)}
                format={values.weekly || values.biweekly ? "EEEE MM/dd/yyyy" : "MM/dd/yyyy"}
              />
            </Box>
          )}
        />
        <Box sx={{ mt: 2 }}>
          <RHFSwitch name="weekly" label="Weekly" />
          {values.weekly && values.shipDate && <Alert sx={{ width: '100%', height: '35px', fontSize: 12, py: 0, px: 1, m: 0 }} severity={"info"}>
            Vendor ships every {new Date(values.shipDate).toLocaleDateString(undefined, { weekday: 'long' })}</Alert>}
        </Box>
        <Box sx={{ mt: 2 }}>
          <RHFSwitch name="biweekly" label="Biweekly" />
          {values.biweekly && values.shipDate && <Alert sx={{ width: '100%', height: '35px', fontSize: 12, py: 0, px: 1, m: 0 }} severity={"info"}>
            Vendor ships every other {new Date(values.shipDate).toLocaleDateString(undefined, { weekday: 'long' })}</Alert>}
        </Box>
      </Stack>

      <DialogActions>
        {isEdit && <Tooltip title="Delete Event">
          <IconButton onClick={onDelete} color="error">
            <Iconify icon="eva:trash-2-outline" />
          </IconButton>
        </Tooltip>}

        <Box sx={{ flexGrow: 1 }} />

        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          loading={isSubmitting}
          disabled={!values.shipDate || (!hasDefaultVendor && !values.vendor) || (!hasDefaultVendor && !values.facility)}
        >
          {isEdit ? 'Update' : 'Add'}
        </LoadingButton>
      </DialogActions>
    </FormProvider>
  );
}
